/**
 * Product Selection Cova - JavaScript
 */

class SelectionSectionCova {
  constructor(section) {
    this.section = section;
    this.carousel = section.querySelector('.js-selection-section-carousel');
    this.carouselContainer = section.querySelector('.selection-section__carousel-container');
    this.wrapper = section.querySelector('.carousel__wrapper');
    this.slides = section.querySelectorAll('.js-selection-section-slide');
    this.prevBtn = section.querySelector('.js-stepper-arrows-prev');
    this.nextBtn = section.querySelector('.js-stepper-arrows-next');
    this.rollerCounter = section.querySelector('.js-pagination-counter');
    this.totalCounter = section.querySelector('.swiper-pagination-total');
    this.visual = section.querySelector('.js-selection-section-visual');
    this.heading = section.querySelector('.selection-section__heading');

    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.scrollProgress = 0;
    this.maxScroll = 0;
    this.isCarouselActive = false;
    this.isTransitioning = false;

    // Variabili per drag fluido
    this.isDragging = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.velocity = 0;
    this.lastX = 0;
    this.lastTime = 0;

    this.init();
  }

  init() {
    if (!this.wrapper || this.slides.length === 0) return;

    // Inizializza lo stato iniziale
    this.section.setAttribute('data-carousel-active', 'false');
    this.isCarouselActive = false;

    this.calculateMaxScroll();
    this.setupEventListeners();
    this.updateNavigation();
    this.updateTransformEffect(0);

    // Inizializza il counter con il valore corretto
    if (this.rollerCounter) {
      this.rollerCounter.textContent = '1';
    }
    if (this.totalCounter) {
      this.totalCounter.textContent = this.totalSlides;
    }

    // Aggiorna al resize (ma non se il carousel è attivo)
    window.addEventListener('resize', this.debounce(() => {
      const isActive = this.section.getAttribute('data-carousel-active') === 'true';
      if (!isActive) {
        this.calculateMaxScroll();
        this.updateNavigation();
      }
    }, 250));
  }

  calculateMaxScroll() {
    // Calcola lo scroll massimo del carousel
    // IMPORTANTE: Calcola sempre basandosi sullo stato iniziale (30% width)
    if (!this.initialMaxScroll) {
      this.initialMaxScroll = this.wrapper.scrollWidth - this.wrapper.clientWidth;
    }
    this.maxScroll = this.wrapper.scrollWidth - this.wrapper.clientWidth;
  }

  setupEventListeners() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }

    // Mouse drag support - Funziona su tutto il carousel container, anche sui prodotti
    const handleMouseDown = (e) => {
      // Ignora solo i pulsanti di navigazione
      if (e.target.closest('.stepper-arrows')) return;

      this.isDragging = true;
      this.hasMoved = false; // Flag per distinguere click da drag
      this.startX = e.pageX;
      this.startY = e.pageY;
      this.scrollLeft = this.wrapper.scrollLeft;
      this.lastX = e.pageX;
      this.lastTime = Date.now();
      this.velocity = 0;

      this.carouselContainer.style.cursor = 'grabbing';
      this.wrapper.style.scrollBehavior = 'auto'; // Disabilita smooth scroll durante drag

      // Previeni selezione testo
      e.preventDefault();
    };

    const handleMouseMove = (e) => {
      if (!this.isDragging) return;

      const deltaX = Math.abs(e.pageX - this.startX);
      const deltaY = Math.abs(e.pageY - this.startY);

      // Se si è mosso più di 5px, considera come drag (non click)
      if (deltaX > 5 || deltaY > 5) {
        this.hasMoved = true;
      }

      e.preventDefault();

      const currentTime = Date.now();
      const deltaTime = currentTime - this.lastTime;
      const deltaXVelocity = e.pageX - this.lastX;

      // Calcola velocità per momentum
      if (deltaTime > 0) {
        this.velocity = deltaXVelocity / deltaTime;
      }

      this.lastX = e.pageX;
      this.lastTime = currentTime;

      const x = e.pageX;
      const walk = (x - this.startX) * 1.5; // Sensibilità aumentata
      const newScrollLeft = this.scrollLeft - walk;

      // Limita lo scroll: non andare oltre l'ultima slide
      const maxScroll = this.wrapper.scrollWidth - this.wrapper.clientWidth;
      const clampedScrollLeft = Math.max(0, Math.min(newScrollLeft, maxScroll));

      this.wrapper.scrollLeft = clampedScrollLeft;
    };

    const handleMouseUp = (e) => {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.carouselContainer.style.cursor = 'grab';
      this.wrapper.style.scrollBehavior = 'smooth';

      // Se è stato un drag (non un semplice click), previeni il click sui link
      if (this.hasMoved) {
        e.preventDefault();
        e.stopPropagation();

        // Applica momentum/inertia
        this.applyMomentum();
      }

      this.hasMoved = false;
    };

    // Previeni click sui link se c'è stato un drag
    const handleClick = (e) => {
      if (this.hasMoved) {
        e.preventDefault();
        e.stopPropagation();
        this.hasMoved = false;
      }
    };

    // Aggiungi eventi al carousel container (non solo al wrapper)
    this.carouselContainer.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    this.carouselContainer.addEventListener('click', handleClick, true); // Capture phase

    // Imposta cursor grab di default
    this.carouselContainer.style.cursor = 'grab';

    // Touch events
    let touchStartX = 0;
    let touchScrollLeft = 0;

    this.wrapper.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].pageX;
      touchScrollLeft = this.wrapper.scrollLeft;
    }, { passive: true });

    this.wrapper.addEventListener('touchmove', (e) => {
      const touchX = e.touches[0].pageX;
      const walk = (touchStartX - touchX);
      const newScrollLeft = touchScrollLeft + walk;

      // Limita lo scroll: non andare oltre l'ultima slide
      const maxScroll = this.wrapper.scrollWidth - this.wrapper.clientWidth;
      const clampedScrollLeft = Math.max(0, Math.min(newScrollLeft, maxScroll));

      this.wrapper.scrollLeft = clampedScrollLeft;
    }, { passive: true });

    this.wrapper.addEventListener('touchend', (e) => {
      // Mobile: swipe = vai alla slide successiva/precedente come con le arrows
      const touchEndX = (e.changedTouches && e.changedTouches[0]) ? e.changedTouches[0].pageX : touchStartX;
      const deltaX = touchStartX - touchEndX; // >0 = swipe verso sinistra (next)
      const MIN_SWIPE = 10; // px
      const maxIndex = this.totalSlides - 1;

      if (Math.abs(deltaX) < MIN_SWIPE) {
        // Spostamento minimo: resta sulla slide corrente (snap al vicino)
        this.snapToNearestSlide();
        return;
      }

      if (deltaX > 0 && this.currentIndex < maxIndex) {
        // Swipe a sinistra: next
        this.currentIndex++;
        this.scrollToIndex(this.currentIndex);
        this.updateCounter(this.currentIndex + 1);
      } else if (deltaX < 0 && this.currentIndex > 0) {
        // Swipe a destra: prev
        this.currentIndex--;
        this.scrollToIndex(this.currentIndex);
        this.updateCounter(this.currentIndex + 1);
      } else {
        this.snapToNearestSlide();
      }
    });

    // CHIAVE DIOR: Update transform effect on scroll (real-time)
    this.wrapper.addEventListener('scroll', () => {
      const scrollLeft = this.wrapper.scrollLeft;
      this.updateTransformEffect(scrollLeft);
      this.updateCurrentIndexFromScroll();
    }, { passive: true });
  }

  prev() {
    const isMobile = window.innerWidth < 1024;

    if (isMobile) {
      // Su mobile: navigazione semplice
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.scrollToIndex(this.currentIndex);
        this.updateCounter(this.currentIndex + 1);
      }
    } else {
      // Su desktop: logica con carousel attivo
      if (this.isCarouselActive) {
        // Se il carousel è attivo, vai alla slide precedente o torna al layout iniziale
        if (this.currentIndex > 0) {
          this.currentIndex--;
          this.scrollToIndex(this.currentIndex);
          this.updateCounter(this.currentIndex + 1);
          // Aggiorna lo stato del layout
          setTimeout(() => {
            this.updateTransformEffect(this.wrapper.scrollLeft);
          }, 100);
        } else {
          // Se siamo già alla slide 0, torna al layout iniziale (32%-28%-40%)
          this.currentIndex = 0;
          this.scrollToIndex(0);
          this.updateCounter(1);
          // Forza il ritorno al layout iniziale
          setTimeout(() => {
            this.updateTransformEffect(0);
          }, 100);
        }
      }
      // Se il carousel non è attivo, non fare nulla (prev è disabilitato)
    }
  }

  next() {
    const maxIndex = this.totalSlides - 1;
    const isMobile = window.innerWidth < 1024;

    if (isMobile) {
      // Su mobile: navigazione semplice
      if (this.currentIndex < maxIndex) {
        this.currentIndex++;
        this.scrollToIndex(this.currentIndex);
        this.updateCounter(this.currentIndex + 1);
      }
    } else {
      // Su desktop: logica con carousel attivo/non attivo
      if (!this.isCarouselActive) {
        // Se il carousel NON è attivo, attivalo (espandi al 100%)
        this.isCarouselActive = true;
        this.section.classList.add('is-scrolling');
        this.section.setAttribute('data-carousel-active', 'true');


        // Emula un "drag" un po' più lungo per superare la soglia di attivazione
        try {
          const slideWidth = this.slides[0]?.offsetWidth || 0;
          const gap = window.innerWidth >= 1024 ? 40 : 20;
          const boostTarget = Math.max(0, Math.min(
            this.wrapper.scrollWidth - this.wrapper.clientWidth,
            Math.round(0.6 * (slideWidth + gap))
          ));
          const prevBehavior = this.wrapper.style.scrollBehavior;
          this.wrapper.style.scrollBehavior = 'auto';
          this.wrapper.scrollLeft = boostTarget;
          this.wrapper.style.scrollBehavior = prevBehavior || 'smooth';
        } catch (e) {}

        // Vai alla slide successiva (slide 1, indice 1)
        this.currentIndex = 1;

        // Dopo la transizione CSS (600ms), scrolla alla slide 1
        setTimeout(() => {
          this.scrollToIndex(this.currentIndex);
          this.updateCounter(this.currentIndex + 1);
          this.calculateMaxScroll();
          this.updateNavigation();
        }, 720);
      } else {
        // Se il carousel è già attivo, navigazione normale
        if (this.currentIndex < maxIndex) {
          this.currentIndex++;
          this.scrollToIndex(this.currentIndex);
          this.updateCounter(this.currentIndex + 1);
          // Aggiorna lo stato del layout
          setTimeout(() => {
            this.updateTransformEffect(this.wrapper.scrollLeft);
          }, 100);
        }
      }
    }
  }

  applyMomentum() {
    // Applica effetto momentum/inertia dopo il rilascio del mouse
    const momentumStrength = 15; // Forza del momentum
    const friction = 0.95; // Attrito (0-1, più alto = meno attrito)
    let currentVelocity = this.velocity * momentumStrength;

    const animate = () => {
      if (Math.abs(currentVelocity) < 0.1) {
        // Momentum terminato, snap alla slide più vicina
        this.snapToNearestSlide();
        return;
      }

      const maxScroll = this.wrapper.scrollWidth - this.wrapper.clientWidth;
      const newScrollLeft = this.wrapper.scrollLeft - currentVelocity;
      const clampedScrollLeft = Math.max(0, Math.min(newScrollLeft, maxScroll));

      this.wrapper.scrollLeft = clampedScrollLeft;

      // Se abbiamo raggiunto i limiti, ferma il momentum
      if (clampedScrollLeft === 0 || clampedScrollLeft === maxScroll) {
        this.snapToNearestSlide();
        return;
      }

      currentVelocity *= friction;
      requestAnimationFrame(animate);
    };

    // Avvia animazione solo se c'è velocità significativa
    if (Math.abs(currentVelocity) > 0.5) {
      requestAnimationFrame(animate);
    } else {
      this.snapToNearestSlide();
    }
  }

  snapToNearestSlide() {
    // Trova la slide più vicina e snap ad essa
    const scrollLeft = this.wrapper.scrollLeft;
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const gap = window.innerWidth >= 1024 ? 40 : 20;
    const slideWithGap = slideWidth + gap;

    // Calcola l'indice della slide più vicina
    const nearestIndex = Math.round(scrollLeft / slideWithGap);
    const clampedIndex = Math.max(0, Math.min(nearestIndex, this.totalSlides - 1));

    // Scroll alla slide più vicina
    if (clampedIndex !== this.currentIndex) {
      this.currentIndex = clampedIndex;
      this.scrollToIndex(this.currentIndex);
      this.updateCounter(this.currentIndex + 1);

      // Aggiorna lo stato del layout basato sulla slide corrente
      setTimeout(() => {
        this.updateTransformEffect(this.wrapper.scrollLeft);
        this.updateNavigation();
      }, 100);
    }
  }

  scrollToIndex(index) {
    if (!this.slides[index]) return;

    const slide = this.slides[index];
    const containerPadding = window.innerWidth >= 1024 ? 40 : 20;
    let scrollLeft = slide.offsetLeft - containerPadding;

    // OTTIMIZZAZIONE: Quando il carousel è attivo e siamo sulla prima slide,
    // scrolla a 0 per mostrare completamente il primo prodotto
    if (this.isCarouselActive && index === 0 && window.innerWidth >= 1024) {
      scrollLeft = 0;
    }

    // MOBILE: Assicura che ogni slide mostri sempre parte della successiva
    // Non modificare lo scrollLeft, il padding e la larghezza ridotta (75vw) gestiscono già questo

    this.wrapper.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });

    this.updateNavigation();
    // Il counter viene aggiornato automaticamente da updateCurrentIndexFromScroll
  }

  updateCurrentIndexFromScroll() {
    const scrollLeft = this.wrapper.scrollLeft;
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const gap = window.innerWidth >= 1024 ? 40 : 20;

    const newIndex = Math.round(scrollLeft / (slideWidth + gap));

    // Aggiorna l'indice solo se è cambiato
    if (newIndex !== this.currentIndex) {
      this.currentIndex = newIndex;
      this.updateNavigation();

      // LOGICA COUNTER: Mostra sempre l'indice reale della slide
      this.updateCounter(this.currentIndex + 1);

      // IMPORTANTE: Aggiorna lo stato del layout quando cambia slide
      this.updateTransformEffect(scrollLeft);
    }
  }

  updateNavigation() {
    const maxIndex = this.totalSlides - 1;
    const isCarouselActive = this.isCarouselActive;
    const isMobile = window.innerWidth < 1024;

    // Update buttons
    if (this.prevBtn) {
      if (isMobile) {
        // Su mobile: prev disabilitato solo se siamo alla prima slide
        this.prevBtn.disabled = this.currentIndex === 0;
        if (this.currentIndex === 0) {
          this.prevBtn.classList.add('stepper-arrows__button--disabled');
        } else {
          this.prevBtn.classList.remove('stepper-arrows__button--disabled');
        }
      } else {
        // Su desktop: logica con carousel attivo/non attivo
        if (isCarouselActive) {
          // Quando il carousel è attivo (100% width):
          // Prev sempre abilitato (per tornare alla slide 1)
          this.prevBtn.disabled = false;
          this.prevBtn.classList.remove('stepper-arrows__button--disabled');
        } else {
          // Quando il carousel NON è attivo (layout 32%-28%-40%):
          // Prev sempre disabilitato (siamo sulla slide 1)
          this.prevBtn.disabled = true;
          this.prevBtn.classList.add('stepper-arrows__button--disabled');
        }
      }
    }

    if (this.nextBtn) {
      if (isMobile) {
        // Su mobile: next disabilitato solo se siamo all'ultima slide
        this.nextBtn.disabled = this.currentIndex >= maxIndex;
        if (this.currentIndex >= maxIndex) {
          this.nextBtn.classList.add('stepper-arrows__button--disabled');
        } else {
          this.nextBtn.classList.remove('stepper-arrows__button--disabled');
        }
      } else {
        // Su desktop: logica con carousel attivo/non attivo
        if (isCarouselActive) {
          // Quando il carousel è attivo:
          // Next disabilitato solo se siamo all'ultima slide
          if (this.currentIndex >= maxIndex) {
            this.nextBtn.disabled = true;
            this.nextBtn.classList.add('stepper-arrows__button--disabled');
          } else {
            this.nextBtn.disabled = false;
            this.nextBtn.classList.remove('stepper-arrows__button--disabled');
          }
        } else {
          // Quando il carousel NON è attivo:
          // Next sempre abilitato (per attivare il carousel)
          this.nextBtn.disabled = false;
          this.nextBtn.classList.remove('stepper-arrows__button--disabled');
        }
      }
    }

    // Update pagination
    if (this.totalCounter) {
      this.totalCounter.textContent = this.totalSlides;
    }
  }

  updateCounter(value) {
    if (this.rollerCounter) {
      // Animazione rolling del counter
      this.rollerCounter.style.transform = 'translateY(-5px)';
      this.rollerCounter.style.opacity = '0';

      setTimeout(() => {
        this.rollerCounter.textContent = value;
        this.rollerCounter.style.transform = 'translateY(0)';
        this.rollerCounter.style.opacity = '1';
      }, 150);
    }
  }

  /**
   * EFFETTO SEMPLIFICATO: Attiva/disattiva lo stato del carousel
   * Layout: 32% + 28% + 40% → 0% + 0% + 100%
   */
  updateTransformEffect(scrollLeft) {
    // Solo su desktop
    if (window.innerWidth < 1024) return;

    // Evita loop durante le transizioni
    if (this.isTransitioning) return;

    // Determina se siamo sulla prima slide
    const isOnFirstSlide = this.currentIndex === 0;

    // Attiva lo slider se NON siamo sulla prima slide
    const shouldActivateSlider = !isOnFirstSlide;

    // Cambia stato solo se necessario
    if (shouldActivateSlider && !this.isCarouselActive) {
      this.isTransitioning = true;
      this.isCarouselActive = true;

      // Attiva lo stato espanso
      this.section.classList.add('is-scrolling');
      this.section.setAttribute('data-carousel-active', 'true');

      // Reset della transizione dopo l'animazione
      setTimeout(() => {
        this.isTransitioning = false;
        this.calculateMaxScroll();
        this.updateNavigation();
      }, 650); // Leggermente più lungo della transizione CSS (600ms)

    } else if (!shouldActivateSlider && this.isCarouselActive) {
      this.isTransitioning = true;
      this.isCarouselActive = false;

      // Disattiva lo stato espanso
      this.section.classList.remove('is-scrolling');
      this.section.setAttribute('data-carousel-active', 'false');

      // Reset della transizione dopo l'animazione
      setTimeout(() => {
        this.isTransitioning = false;
        this.calculateMaxScroll();
      }, 650);
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize all selection sections
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.js-selection-section');
  sections.forEach(section => {
    new SelectionSectionCova(section);
  });
});

// Shopify theme editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.js-selection-section');
    if (section) {
      new SelectionSectionCova(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    // Cleanup if needed
  });
}

